import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { SpeakerMappings } from "../../types/avatar_appearances.d";

interface SpeakerIdentificationTableProps {
  avatarAppearanceId: string;
  uniqueSpeakers: string[];
  initialMappings: SpeakerMappings;
  avatarId: string;
  avatarDisplayName: string;
}

interface SavingState {
  [speakerId: string]: "idle" | "saving" | "success" | "error";
}

export default function SpeakerIdentificationTable({
  avatarAppearanceId,
  uniqueSpeakers,
  initialMappings,
  avatarId,
  avatarDisplayName,
}: SpeakerIdentificationTableProps) {
  const [speakerMappings, setSpeakerMappings] =
    useState<SpeakerMappings>(initialMappings);
  const [savingStates, setSavingStates] = useState<SavingState>({});

  const saveMappingForSpeaker = async (
    speakerId: string,
    mapping: SpeakerMappings[string]
  ) => {
    setSavingStates((prev) => ({ ...prev, [speakerId]: "saving" }));

    try {
      const response = await fetch(
        `/api/v1/avatar_appearances/${avatarAppearanceId}/update_speaker_mappings`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify({
            speaker_mappings: {
              ...speakerMappings,
              [speakerId]: mapping,
            },
          }),
        }
      );

      if (response.ok) {
        setSpeakerMappings((prev) => ({
          ...prev,
          [speakerId]: mapping,
        }));
        setSavingStates((prev) => ({ ...prev, [speakerId]: "success" }));
        setTimeout(() => {
          setSavingStates((prev) => ({ ...prev, [speakerId]: "idle" }));
        }, 2000);
      } else {
        setSavingStates((prev) => ({ ...prev, [speakerId]: "error" }));
        setTimeout(() => {
          setSavingStates((prev) => ({ ...prev, [speakerId]: "idle" }));
        }, 3000);
      }
    } catch (error) {
      console.error("Error saving speaker mapping:", error);
      setSavingStates((prev) => ({ ...prev, [speakerId]: "error" }));
      setTimeout(() => {
        setSavingStates((prev) => ({ ...prev, [speakerId]: "idle" }));
      }, 3000);
    }
  };

  const handleAvatarClick = (speakerId: string) => {
    const mapping = {
      type: "avatar" as const,
      avatarId,
      displayName: avatarDisplayName,
    };
    saveMappingForSpeaker(speakerId, mapping);
  };

  const handleOtherChange = (speakerId: string, value: string) => {
    if (value.trim()) {
      const mapping = {
        type: "other" as const,
        displayName: value.trim(),
      };
      saveMappingForSpeaker(speakerId, mapping);
    }
  };

  const getSavingIcon = (speakerId: string) => {
    const state = savingStates[speakerId];
    if (state === "saving") {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (state === "success") {
      return <Check className="w-4 h-4 text-success" />;
    }
    return null;
  };

  return (
    <div className="mt-6">
      <h2 className="text-xl font-semibold mb-4">Speaker Identification</h2>
      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Speaker</th>
              <th>Assignment</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {uniqueSpeakers.map((speakerId) => {
              const mapping = speakerMappings[speakerId];
              const isAvatar = mapping?.type === "avatar";
              const isOther = mapping?.type === "other";

              return (
                <tr key={speakerId}>
                  <td className="font-mono">{speakerId}</td>
                  <td>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => handleAvatarClick(speakerId)}
                        className={`btn btn-sm ${
                          isAvatar ? "btn-primary" : "btn-outline"
                        }`}
                        disabled={savingStates[speakerId] === "saving"}
                      >
                        {avatarDisplayName}
                      </button>
                      <span className="text-base-content/50">or</span>
                      <input
                        type="text"
                        placeholder="Other (e.g., User, Customer)"
                        defaultValue={isOther ? mapping.displayName || "" : ""}
                        onBlur={(e) =>
                          handleOtherChange(speakerId, e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleOtherChange(speakerId, e.currentTarget.value);
                          }
                        }}
                        className={`input input-sm input-bordered flex-1 ${
                          isOther ? "input-primary" : ""
                        }`}
                        disabled={savingStates[speakerId] === "saving"}
                      />
                    </div>
                  </td>
                  <td>{getSavingIcon(speakerId)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
